import React, { useEffect, useMemo, useState } from "react";
import style from "./index.module.scss";
import {
    Col,
    Icon,
    Input,
    InputNumber,
    Modal,
    Row,
    Spin,
    Tooltip,
    DatePicker,
    message
} from "antd";
import moment from "moment";
import ProjectSelect from "./components/ProjectSelect";
import ProjectListModal from "../ProjectListModal";
import _ from "underscore";
import {
    addCloudDeskTop,
    CloudDeskInfo,
    getCloudDeskPersonNum
} from "service/experiment";
import { CourseItem, errorMessage } from "service/service";
import useRequest from "utils/useRequest";
import { ResultMessage } from "../../../ResultMessage";

interface SettingModalProps {
    course: CourseItem;
    visible: boolean;
    onCancel: () => void;
    onOk: () => void;
}

export default function SettingModal({
    course,
    visible,
    onCancel,
    onOk
}: SettingModalProps) {
    const { RangePicker } = DatePicker;
    const [num, ,] = useRequest(() =>
        getCloudDeskPersonNum(course.courseNo, course.courseSeq)
    );

    const [title, setTitle] = useState(""); //项目名称
    const [orderNum, setOrderNum] = useState<number>(1); //课程人数
    const [projectId, setProjectId] = useState<string | undefined>(); //实验项目id
    const [startTime, setStartTime] = useState(""); //实验开始时间
    const [endTime, setEndTime] = useState(""); //实验结束时间
    const [projectName, setProjectName] = useState<string>(); //实验项目名字
    const [cloudGuid, setCloudGuid] = useState<string>(); //cloudGuid
    const [cloudDeskInfo, setCloudDeskInfo] = useState<CloudDeskInfo>(); //云桌面数据集合
    const [dataLength, setDataLength] = useState(0); //实验项目数量

    const [isShow, setIsShow] = useState(false); //是否展示资源列表，默认不展示
    const [loading, setLoading] = useState(false);
    const [isAfter, setIsAfter] = useState(false); //课表时间是否在当前时间之后
    const [error, setError] = useState({ projectName: "", projectTime: "" }); // 错误状态

    useEffect(() => {
        if (num) {
            setOrderNum(num);
        }
    }, [num]);

    //整理当前课表时间
    const timeRange = useMemo(() => {
        const currentDate = moment(course.date).format("YYYY-MM-DD");
        const start = moment(currentDate + " " + course.beginTime).format(
            "YYYY-MM-DD HH:mm"
        );
        const end = moment(currentDate + " " + course.endTime).format(
            "YYYY-MM-DD HH:mm"
        );
        return { start, end };
    }, [course]);

    //课表时间在当前时间之后，点击添加任务按钮，将当前课表时间带到日期选择器中
    useEffect(() => {
        if (!moment(timeRange.end).isBefore(moment())) {
            setIsAfter(true);
            setStartTime(timeRange.start);
            setEndTime(timeRange.end);
        }
    }, [timeRange]);

    //日期选择禁选当前日期之前的日期
    function disabledDate(current: any) {
        return current && current < moment().subtract(1, "days").endOf("day");
    }

    //限制课程人数不为小数和0以下的数
    const limitDecimals = (value: any) => {
        if (typeof value === "string") {
            return value.replace(/^[0+]|[^\d]+/g, "");
        }
        return value;
    };

    async function addHandler() {
        if (!checkData()) return;
        setLoading(true);

        const params = {
            cloudGuid: cloudGuid ? cloudGuid : "",
            name:
                title?.trim().length === 0
                    ? moment().format("MM-DD HH:mm") + " 实验计划"
                    : title,
            useCount: String(orderNum),
            resId: projectId || "",
            beginTime: startTime,
            endTime: endTime,
            weekTimeId: String(course.weekTimeId),
            osName: cloudDeskInfo ? cloudDeskInfo.OsName : ""
        };

        try {
            await addCloudDeskTop({ ...params });
            ResultMessage.success("添加成功!");
            onOk();
            onCancel();
            handleClose();
        } catch (e) {
            message.error(errorMessage(e));
        }

        setLoading(false);
    }

    // 检查数据
    function checkData(): boolean {
        const err = { projectName: "", projectTime: "" };

        if (projectId === undefined) {
            err.projectName = "实验项目为必填项，请选择实验项目";
        }

        if (startTime?.trim().length === 0 || endTime?.trim().length === 0) {
            err.projectTime = "实验时间必填，请选择实验时间";
        }

        setError(err);
        return _.every(Object.values(err), msg => msg.length === 0);
    }

    //关闭时清空表单
    function handleClose() {
        onCancel();
        setTitle("");
        setOrderNum(num);
        setProjectId(dataLength !== 1 ? undefined : projectId);
        setStartTime(isAfter ? timeRange.start : "");
        setEndTime(isAfter ? timeRange.end : "");
        setError({ projectName: "", projectTime: "" });
    }

    //实验项目改变
    function onProjectChange(e: string) {
        setProjectId(e);
        if (e !== undefined) setError({ ...error, projectName: "" });
    }

    //实验时间改变
    function onProjectTimeChange(dates: any) {
        const start =
            dates.length !== 0
                ? moment(dates?.[0]).format("YYYY-MM-DD HH:mm")
                : "";
        const end =
            dates.length !== 0
                ? moment(dates?.[1]).format("YYYY-MM-DD HH:mm")
                : "";
        setStartTime(start);
        setEndTime(end);
        if (start.length !== 0 || endTime.length !== 0)
            setError({ ...error, projectTime: "" });
    }

    return (
        <>
            <Modal
                visible={visible}
                title="新建实验计划"
                onCancel={handleClose}
                onOk={addHandler}
                className={style.root}
                width={610}
                confirmLoading={loading}
                maskClosable={false}>
                <Spin spinning={loading}>
                    <Row>
                        <Col span={6} className={style.label}>
                            名称：
                        </Col>
                        <Col span={15} className={style.label}>
                            <Input
                                placeholder="请输入实验使用计划名称"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                maxLength={500}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col span={6} className={style.label}>
                            预约人数：
                        </Col>
                        <Col span={15} className={style.label}>
                            <InputNumber
                                min={1}
                                max={999}
                                value={orderNum}
                                onChange={e => setOrderNum(e || 0)}
                                formatter={limitDecimals}
                            />
                        </Col>
                        <Col span={3}>
                            <Tooltip
                                placement="top"
                                title={
                                    "预约数量=课程人数+1，老师可根据实际需要手动调整预约数量"
                                }>
                                <Icon type="info-circle" />
                            </Tooltip>
                        </Col>
                    </Row>

                    <Row>
                        <Col span={6} className={style.label}>
                            <i className={style.warning}>*</i>实验项目：
                        </Col>
                        <Col span={15} className={style.label}>
                            <ProjectSelect
                                courseNo={course.courseNo}
                                courseSeq={course.courseSeq}
                                projectId={projectId}
                                onChange={onProjectChange}
                                setCloudDeskInfo={setCloudDeskInfo}
                                setProjectName={setProjectName}
                                onShow={() => setIsShow(true)}
                                setCloudGuid={setCloudGuid}
                                setDataLength={setDataLength}
                            />
                            {error.projectName && (
                                <div className={style.error}>
                                    {error.projectName}
                                </div>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col span={6} className={style.label}>
                            <i className={style.warning}>*</i>实验时间：
                        </Col>
                        <Col span={15} className={style.label}>
                            <RangePicker
                                disabledDate={disabledDate}
                                value={
                                    !startTime
                                        ? [null, null]
                                        : [
                                              moment(
                                                  startTime,
                                                  "YYYY-MM-DD HH:mm"
                                              ),
                                              moment(
                                                  endTime,
                                                  "YYYY-MM-DD HH:mm"
                                              )
                                          ]
                                }
                                showTime={{
                                    hideDisabledOptions: true
                                }}
                                format="YYYY-MM-DD HH:mm"
                                onChange={onProjectTimeChange}
                                className={style.rangePicker}
                            />
                            {error.projectTime && (
                                <div className={style.error}>
                                    {error.projectTime}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Spin>
            </Modal>
            <ProjectListModal
                isShow={isShow}
                onChange={() => setIsShow(false)}
                projectName={projectName}
                cloudGuid={cloudDeskInfo?.CloudGuid}
                desktopCount={cloudDeskInfo?.DesktopCount}
                nameLabel={cloudDeskInfo?.Name_label}
            />
        </>
    );
}
